<template>
  <div class="rp-page">
    <div class="rp-container">
      <div class="rp-header">
        <div class="d-flex align-items-center">
          <button class="rp-back" @click="$router.back()"><b-icon icon="chevron-left"></b-icon></button>
          <h2 class="rp-title"><b-icon icon="bar-chart-line" class="mr-2"></b-icon>Partiya (seriya) hisoboti</h2>
        </div>
        <div class="d-flex" style="gap: 8px;">
          <button class="rp-btn rp-btn-secondary" @click="exportCsv" :disabled="loading || items.length === 0">
            <b-icon icon="file-earmark-excel"></b-icon> Excel
          </button>
        </div>
      </div>

      <p class="pt-hint">
        Faqat kirim hujjatlari orqali kelgan seriyalar hisoblanadi. Kirimgacha omborda bo'lgan qoldiq va uning sotuvi
        bu hisobotga kirmaydi. Sotuv FIFO bo'yicha: avval eski seriya sotiladi.
      </p>

      <div class="rp-filters">
        <div class="rp-filter"><label>Kirim sanasi (dan)</label><input type="date" v-model="date1"></div>
        <div class="rp-filter"><label>Kirim sanasi (gacha)</label><input type="date" v-model="date2"></div>
        <div class="rp-filter rp-filter-grow">
          <label>Papka</label>
          <v-select :options="folderList" v-model="folder_id" :reduce="f => f.id" label="extra_name" placeholder="Barchasi" />
        </div>
        <div class="rp-filter rp-filter-grow">
          <label>Kirim hujjati</label>
          <v-select :options="prixodOptions" v-model="prixod_id" :reduce="p => p.id" label="label" placeholder="Barchasi" />
        </div>
        <div class="rp-filter rp-filter-grow">
          <label>Ta'minotchi</label>
          <v-select :options="supplierList" v-model="kontragent_id" :reduce="k => k.id" label="name" placeholder="Barchasi" />
        </div>
        <div class="rp-filter"><label>&nbsp;</label>
          <button class="rp-btn rp-btn-primary" @click="fetch" :disabled="loading"><b-icon icon="arrow-repeat"></b-icon> Ko'rsatish</button>
        </div>
      </div>

      <div class="rp-summary pt-summary-7">
        <div class="rp-card rp-c-1">
          <div class="rp-card-label">KELGAN TOVAR (TANNARX)</div>
          <div class="rp-card-value">{{ fmt(totals.landed_summa) }}</div>
          <div class="pt-card-sub">{{ fmt(totals.kirim_count) }} dona · ta'minotchi narxida {{ fmt(totals.purchase_summa) }}</div>
        </div>
        <div class="rp-card rp-c-6">
          <div class="rp-card-label">XARAJATLAR</div>
          <div class="rp-card-value rp-orange">{{ fmt(totals.expense_summa) }}</div>
          <div class="pt-card-sub">{{ pct(totals.expense_summa, totals.purchase_summa) }} ta'minotchi narxiga nisbatan</div>
        </div>
        <div class="rp-card rp-c-2">
          <div class="rp-card-label">TO'LANGAN SOTUV</div>
          <div class="rp-card-value rp-green">{{ fmt(totals.paid_summa) }}</div>
          <div class="pt-card-sub">{{ fmt(totals.sold_count) }} dona · tannarxi {{ fmt(totals.sold_cost) }}</div>
        </div>
        <div class="rp-card pt-credit-card">
          <div class="rp-card-label">QARZGA SOTUV</div>
          <div class="rp-card-value rp-orange">{{ fmt(totals.credit_summa) }}</div>
          <div class="pt-card-sub">Umumiy sotuv {{ fmt(totals.sold_summa) }}</div>
        </div>
        <div class="rp-card rp-c-4">
          <div class="rp-card-label">FOYDA</div>
          <div class="rp-card-value" :class="totals.profit < 0 ? 'rp-red' : 'rp-blue'">{{ fmt(totals.profit) }}</div>
          <div class="pt-card-sub">{{ pct(totals.profit, totals.sold_cost) }} ustama</div>
        </div>
        <div class="rp-card rp-c-3">
          <div class="rp-card-label">QOLDIQ (TANNARX)</div>
          <div class="rp-card-value rp-red">{{ fmt(totals.balance_summa) }}</div>
          <div class="pt-card-sub">{{ fmt(totals.balance_count) }} dona · chakana narxda {{ fmt(totals.balance_chakana) }}</div>
        </div>
        <div class="rp-card rp-c-5">
          <div class="rp-card-label">BOSHQA CHIQIM</div>
          <div class="rp-card-value">{{ fmt(totals.other_count) }} <small>dona</small></div>
          <div class="pt-card-sub">spisaniya, ta'minotchiga qaytarish, reviziya</div>
        </div>
      </div>

      <!-- Bitta kirim hujjati tanlanganda: hujjat ma'lumoti -->
      <div v-if="prixod" class="rp-section">
        <h3 class="rp-block-title"><b-icon icon="truck"></b-icon>
          Kirim №{{ prixod.number }} · {{ fmtDate(prixod.created_at) }} · {{ prixod.kontragent ? prixod.kontragent.name : '' }}
        </h3>
        <div class="pt-doc">
          <div>Kirim summasi: <b>{{ fmt(prixod.summa) }}</b></div>
          <div v-if="parseFloat(prixod.rasxod_summa)" class="pt-exp">
            Rasxod: <b>{{ fmt(prixod.rasxod_summa) }} so'm</b>
            <span class="rp-muted"> · mahsulotlar tannarxiga proporsional sochilgan</span>
          </div>
          <div v-else class="rp-muted">Bu kirimda rasxod kiritilmagan.</div>
          <button class="rp-btn rp-btn-secondary mt-2" @click="$router.push('/kirim/update/' + prixod.id)">
            <b-icon icon="pencil-square"></b-icon> Kirimni ochish
          </button>
        </div>
      </div>

      <div class="rp-section">
        <div class="pt-toolbar">
          <h3 class="rp-block-title mb-0"><b-icon icon="list-ul"></b-icon> Seriyalar</h3>
          <div class="pt-group">
            <span>Guruhlash:</span>
            <button v-for="g in groupModes" :key="g.value" :class="['pt-gbtn', { active: groupBy === g.value }]" @click="groupBy = g.value">{{ g.label }}</button>
          </div>
          <input type="search" v-model="search" class="pt-search" placeholder="Mahsulot qidirish...">
        </div>
        <div class="rp-table-wrap">
          <table class="rp-table pt-table">
            <thead>
              <tr>
                <th>Kirim</th><th>Ta'minotchi</th><th>Mahsulot</th><th>Papka</th>
                <th class="t-right">Keldi</th>
                <th class="t-right">Kelish narxi</th>
                <th class="t-right">Xarajat (1 dona)</th>
                <th class="t-right">Tushgan narx</th>
                <th class="t-right">Kirim summasi</th>
                <th class="t-right">Sotildi</th>
                <th class="t-right">O'rt. sotuv narxi</th>
                <th class="t-right">To'langan sotuv</th>
                <th class="t-right">Qarzga sotuv</th>
                <th class="t-right">Foyda</th>
                <th class="t-right">Boshqa chiqim</th>
                <th class="t-right">Qoldiq</th>
                <th class="t-right">Qoldiq summasi</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="g in groups">
                <tr v-if="groupBy !== 'none'" :key="'g' + g.key" class="pt-grow" @click="toggle(g.key)">
                  <td colspan="4">
                    <b-icon :icon="opened[g.key] ? 'chevron-down' : 'chevron-right'"></b-icon>
                    <b>{{ g.title }}</b> <span class="rp-muted">({{ g.rows.length }})</span>
                  </td>
                  <td class="t-right">{{ fmt(g.sum.kirim_count) }}</td>
                  <td class="t-right">{{ fmt(div(g.sum.purchase_summa, g.sum.kirim_count)) }}</td>
                  <td class="t-right">{{ fmt(div(g.sum.expense_summa, g.sum.kirim_count)) }}</td>
                  <td class="t-right">{{ fmt(div(g.sum.landed_summa, g.sum.kirim_count)) }}</td>
                  <td class="t-right rp-strong">{{ fmt(g.sum.landed_summa) }}</td>
                  <td class="t-right">{{ fmt(g.sum.sold_count) }}</td>
                  <td class="t-right">{{ fmt(div(g.sum.sold_summa, g.sum.sold_count)) }}</td>
                  <td class="t-right rp-green rp-strong">{{ fmt(g.sum.paid_summa) }}</td>
                  <td class="t-right rp-orange rp-strong">{{ fmt(g.sum.credit_summa) }}</td>
                  <td class="t-right rp-strong" :class="g.sum.profit < 0 ? 'rp-red' : 'rp-blue'">{{ fmt(g.sum.profit) }}</td>
                  <td class="t-right">{{ fmt(g.sum.other_count) }}</td>
                  <td class="t-right rp-strong">{{ fmt(g.sum.balance_count) }}</td>
                  <td class="t-right rp-red rp-strong">{{ fmt(g.sum.balance_summa) }}</td>
                </tr>
                <template v-if="groupBy === 'none' || opened[g.key]">
                  <tr v-for="r in g.rows" :key="'r' + r.row_id" :class="{ 'pt-child': groupBy !== 'none' }">
                    <td>
                      <a href="#" @click.prevent="openPrixod(r.prixod_id)">№{{ r.prixod_number }}</a>
                      <div class="rp-muted pt-small">{{ fmtDate(r.prixod_date) }}</div>
                    </td>
                    <td>{{ r.kontragent_name }}</td>
                    <td class="rp-strong">{{ r.product_name }}</td>
                    <td>{{ r.folder_name || '—' }}</td>
                    <td class="t-right">{{ fmt(r.kirim_count) }}</td>
                    <td class="t-right">
                      {{ fmt(r.purchase_price) }}
                      <div v-if="r.currency === 'USD'" class="rp-muted pt-small">{{ fmt(r.original_price) }}$ × {{ fmt(r.dollar_rate) }}</div>
                    </td>
                    <td class="t-right rp-orange">{{ fmt(r.expense_price) }}</td>
                    <td class="t-right rp-strong">{{ fmt(r.landed_price) }}</td>
                    <td class="t-right">{{ fmt(r.landed_summa) }}</td>
                    <td class="t-right">{{ fmt(r.sold_count) }}</td>
                    <td class="t-right">{{ fmt(r.avg_sale_price) }}</td>
                    <td class="t-right rp-green">{{ fmt(r.paid_summa) }}</td>
                    <td class="t-right rp-orange">{{ fmt(r.credit_summa) }}</td>
                    <td class="t-right" :class="r.profit < 0 ? 'rp-red' : 'rp-blue'">{{ fmt(r.profit) }}</td>
                    <td class="t-right">{{ fmt(r.other_count) }}</td>
                    <td class="t-right rp-strong">{{ fmt(r.balance_count) }}</td>
                    <td class="t-right rp-red">{{ fmt(r.balance_summa) }}</td>
                  </tr>
                </template>
              </template>
              <tr v-if="!loading && filtered.length === 0"><td colspan="17" class="rp-empty">Ma'lumot topilmadi</td></tr>
              <tr v-if="loading"><td colspan="17" class="rp-empty">Yuklanmoqda...</td></tr>
            </tbody>
            <tfoot v-if="filtered.length > 0">
              <tr>
                <th colspan="4" class="t-right">JAMI:</th>
                <th class="t-right">{{ fmt(shown.kirim_count) }}</th>
                <th></th><th></th><th></th>
                <th class="t-right">{{ fmt(shown.landed_summa) }}</th>
                <th class="t-right">{{ fmt(shown.sold_count) }}</th>
                <th></th>
                <th class="t-right">{{ fmt(shown.paid_summa) }}</th>
                <th class="t-right">{{ fmt(shown.credit_summa) }}</th>
                <th class="t-right">{{ fmt(shown.profit) }}</th>
                <th class="t-right">{{ fmt(shown.other_count) }}</th>
                <th class="t-right">{{ fmt(shown.balance_count) }}</th>
                <th class="t-right">{{ fmt(shown.balance_summa) }}</th>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import './report.css';

const SUM_KEYS = ['kirim_count', 'purchase_summa', 'expense_summa', 'landed_summa', 'sold_count', 'sold_summa', 'paid_summa', 'credit_summa',
  'sold_cost', 'profit', 'other_count', 'balance_count', 'balance_summa', 'balance_chakana'];
const pad = n => String(n).padStart(2, '0');
const dateStr = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const sumRows = rows => {
  const s = {};
  SUM_KEYS.forEach(k => { s[k] = rows.reduce((a, r) => a + (parseFloat(r[k]) || 0), 0); });
  return s;
};

export default {
  data() {
    const q = this.$route.query;
    const hasTarget = !!(q.prixod_id || q.folder_id);
    return {
      // Kirim yoki papka bo'yicha ochilganda — sana cheklovisiz (butun partiya)
      date1: hasTarget ? '' : `${new Date().getFullYear()}-01-01`,
      date2: hasTarget ? '' : dateStr(new Date()),
      folder_id: q.folder_id ? Number(q.folder_id) : null,
      prixod_id: q.prixod_id ? Number(q.prixod_id) : null,
      kontragent_id: null,
      folderList: [], prixodList: [],
      items: [], totals: sumRows([]), prixod: null,
      groupBy: q.prixod_id ? 'none' : (q.folder_id ? 'product' : 'prixod'),
      groupModes: [
        { value: 'none', label: 'Seriya' },
        { value: 'prixod', label: 'Kirim hujjati' },
        { value: 'product', label: 'Mahsulot' },
        { value: 'folder', label: 'Papka' },
      ],
      opened: {}, search: '', loading: false,
    };
  },
  computed: {
    prixodOptions() {
      return this.prixodList.map(p => ({
        id: p.id,
        label: `№${p.number} · ${this.fmtDate(p.created_at)} · ${p.kontragent ? p.kontragent.name : ''}`,
      }));
    },
    supplierList() {
      const seen = {};
      return this.prixodList.filter(p => p.kontragent_id && !seen[p.kontragent_id] && (seen[p.kontragent_id] = true))
        .map(p => ({ id: p.kontragent_id, name: p.kontragent ? p.kontragent.name : '#' + p.kontragent_id }));
    },
    filtered() {
      if (!this.search || this.search.length < 2) return this.items;
      const s = this.search.toLowerCase();
      return this.items.filter(r => (r.product_name || '').toLowerCase().includes(s));
    },
    shown() { return sumRows(this.filtered); },
    groups() {
      if (this.groupBy === 'none') return [{ key: 'all', title: '', rows: this.filtered, sum: this.shown }];
      const map = new Map();
      for (const r of this.filtered) {
        let key, title;
        if (this.groupBy === 'prixod') { key = r.prixod_id; title = `Kirim №${r.prixod_number} · ${this.fmtDate(r.prixod_date)} · ${r.kontragent_name || ''}`; }
        else if (this.groupBy === 'product') { key = r.product_id; title = r.product_name; }
        else { key = r.folder_id || 0; title = r.folder_name || 'Papkasiz'; }
        if (!map.has(key)) map.set(key, { key, title, rows: [] });
        map.get(key).rows.push(r);
      }
      return [...map.values()].map(g => ({ ...g, sum: sumRows(g.rows) }));
    },
  },
  watch: {
    groupBy() { this.opened = {}; },
  },
  mounted() {
    this.loadLists();
    this.fetch();
  },
  methods: {
    fmt(v) { return (parseFloat(v) || 0).toLocaleString('ru-RU', { maximumFractionDigits: 2 }); },
    fmtDate(u) { return u ? dateStr(new Date(u * 1000)).split('-').reverse().join('.') : ''; },
    div(a, b) { return b ? a / b : 0; },
    pct(a, b) { return b ? (a / b * 100).toFixed(1) + '%' : '—'; },
    toUnix(s, end) { return s ? Math.floor(new Date(s + (end ? 'T23:59:59' : 'T00:00:00')).getTime() / 1000) : null; },
    toggle(key) { this.$set(this.opened, key, !this.opened[key]); },
    async loadLists() {
      const get = url => axios.get(url).then(r => r.data || []).catch(() => []);
      const [folders, prixods] = await Promise.all([get('api/v1/product/folder-list'), get('api/v1/prixod')]);
      this.folderList = folders;
      this.prixodList = prixods;
    },
    openPrixod(id) {
      this.prixod_id = id;
      this.date1 = ''; this.date2 = '';
      this.groupBy = 'none';
      this.fetch();
    },
    async fetch() {
      this.loading = true;
      try {
        const r = await axios.post('api/v1/lot-report/series', {
          datetime1: this.toUnix(this.date1, false),
          datetime2: this.toUnix(this.date2, true),
          folder_id: this.folder_id || null,
          prixod_id: this.prixod_id || null,
          kontragent_id: this.kontragent_id || null,
        });
        this.items = r.data.items || [];
        this.totals = r.data.totals || sumRows([]);
        this.prixod = r.data.prixod || null;
      } catch (e) {
        this.$store.state.errors = (e.response && e.response.data && e.response.data.message) || 'Hisobot yuklanmadi';
      } finally { this.loading = false; }
    },
    exportCsv() {
      const h = ['Kirim №', 'Sana', "Ta'minotchi", 'Mahsulot', 'Papka', 'Keldi', 'Kelish narxi', 'Xarajat (1 dona)',
        'Tushgan narx', 'Kirim summasi', 'Sotildi', "O'rt. sotuv narxi", "To'langan sotuv", 'Qarzga sotuv', 'Foyda', 'Boshqa chiqim',
        'Qoldiq', 'Qoldiq summasi', 'Qoldiq (chakana)'];
      const n = v => String(Math.round((parseFloat(v) || 0) * 100) / 100).replace('.', ',');
      const rows = this.filtered.map(r => [r.prixod_number, this.fmtDate(r.prixod_date), r.kontragent_name, r.product_name,
        r.folder_name, n(r.kirim_count), n(r.purchase_price), n(r.expense_price), n(r.landed_price), n(r.landed_summa),
        n(r.sold_count), n(r.avg_sale_price), n(r.paid_summa), n(r.credit_summa), n(r.profit), n(r.other_count), n(r.balance_count),
        n(r.balance_summa), n(r.balance_chakana)]);
      const csv = [h, ...rows].map(x => x.map(c => `"${String(c == null ? '' : c).replace(/"/g, '""')}"`).join(';')).join('\n');
      const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `partiya_hisoboti_${dateStr(new Date())}.csv`;
      a.click();
    },
  },
};
</script>

<style scoped>
.pt-hint { background: #eff6ff; border: 1px solid #bfdbfe; color: #1e3a8a; border-radius: 12px; padding: 10px 14px; font-size: 13px; margin-bottom: 14px; }
.pt-card-sub { font-size: 12px; color: #64748b; margin-top: 4px; }
.pt-summary-7 { grid-template-columns: repeat(7, minmax(0, 1fr)); }
.pt-credit-card { border-top-color: #ea580c; }
.pt-doc { font-size: 14px; display: flex; flex-direction: column; gap: 4px; }
.pt-exp { padding-left: 10px; border-left: 3px solid #f59e0b; }
.pt-toolbar { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; margin-bottom: 10px; }
.pt-group { display: flex; align-items: center; gap: 6px; font-size: 13px; color: #64748b; }
.pt-gbtn { border: 1px solid #e2e8f0; background: #fff; border-radius: 8px; padding: 4px 10px; font-size: 13px; cursor: pointer; }
.pt-gbtn.active { background: #2563eb; border-color: #2563eb; color: #fff; }
.pt-search { margin-left: auto; height: 34px; border: 1px solid #e2e8f0; border-radius: 10px; padding: 0 10px; min-width: 220px; }
.pt-table { font-size: 13px; }
.pt-table th, .pt-table td { white-space: nowrap; }
.pt-grow { background: #f8fafc; cursor: pointer; }
.pt-grow:hover { background: #f1f5f9; }
.pt-child td:first-child { padding-left: 28px; }
.pt-small { font-size: 11px; }
@media (max-width: 1200px) { .pt-summary-7 { grid-template-columns: repeat(4, minmax(0, 1fr)); } }
@media (max-width: 767px) { .pt-summary-7 { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
</style>
